import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Send, NotebookPen, Check, Loader2, X, Clock } from "lucide-react";
import { toast } from "sonner";
import { API } from "./coach/SharedComponents";

/**
 * AtRiskAthletesPanel — surfaces previously-engaged athletes who haven't
 * logged a workout in 7+ days. Each row supports two coach actions:
 *   1) Send a check-in (creates an in-app mission + email)
 *   2) Leave a weekly note (shown on athlete dashboard through the week)
 */
export const AtRiskAthletesPanel = () => {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteModal, setNoteModal] = useState(null); // athlete object
  const [checkInBusy, setCheckInBusy] = useState(null); // athlete_id while sending

  useEffect(() => {
    fetchAtRisk();
  }, []);

  const fetchAtRisk = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/coach/at-risk-athletes`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setAthletes(d.athletes || []);
      }
    } catch (e) {
      console.error("at-risk fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (athlete) => {
    if (checkInBusy) return;
    setCheckInBusy(athlete.user_id);
    try {
      const r = await fetch(`${API}/coach/athletes/${athlete.user_id}/check-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (r.ok) {
        toast.success(`Check-in sent to ${athlete.name.split(" ")[0]}`);
      } else {
        toast.error("Couldn't send — please try again");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setCheckInBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#141B2D] border border-[#1E293B] rounded-2xl md:rounded-3xl p-5 md:p-6 h-80 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-[#141B2D] border border-[#1E293B] rounded-2xl md:rounded-3xl p-5 md:p-6" data-testid="at-risk-panel">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-white">At-Risk Athletes</h2>
              <p className="text-xs text-gray-500">7+ days since last workout</p>
            </div>
          </div>
          <span className="text-xs font-semibold bg-amber-500/10 text-amber-400 px-2.5 py-1 rounded-full">
            {athletes.length}
          </span>
        </div>

        {athletes.length === 0 ? (
          <div className="py-12 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 mx-auto mb-3 flex items-center justify-center">
              <Check className="w-7 h-7 text-emerald-400" />
            </div>
            <p className="text-white font-semibold">All athletes training consistently</p>
            <p className="text-sm text-gray-500 mt-1">No one's slipped past 7 days. Great week.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {athletes.map((a) => (
              <motion.div
                key={a.user_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-[#0B0F1A] border border-[#1E293B] rounded-xl hover:border-amber-500/30 transition-colors"
                data-testid={`at-risk-athlete-${a.user_id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600/30 to-blue-500/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-white">
                      {(a.name || a.email || "?").slice(0, 1).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-white truncate">{a.name || a.email}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      <Clock className="w-3 h-3" />
                      <span>{a.days_since_last} days</span>
                      <span className="text-gray-700">·</span>
                      <span>{a.total_workouts} total workouts</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => setNoteModal(a)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-[#1E293B] hover:bg-[#2D3B4F] text-gray-300 text-xs font-semibold rounded-lg transition-colors"
                    data-testid={`leave-note-${a.user_id}`}
                  >
                    <NotebookPen className="w-3.5 h-3.5" />
                    Note
                  </button>
                  <button
                    onClick={() => handleCheckIn(a)}
                    disabled={checkInBusy === a.user_id}
                    className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:shadow-lg hover:shadow-blue-500/25 disabled:opacity-60 text-white text-xs font-semibold rounded-lg transition-all"
                    data-testid={`send-checkin-${a.user_id}`}
                  >
                    {checkInBusy === a.user_id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    Check In
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {noteModal && (
        <CoachNoteModal
          athlete={noteModal}
          onClose={() => setNoteModal(null)}
        />
      )}
    </>
  );
};

// ====================================================================
// CoachNoteModal — write/edit the weekly note for an athlete
// ====================================================================
export const CoachNoteModal = ({ athlete, onClose }) => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  const [goals, setGoals] = useState([]);
  const [foodToday, setFoodToday] = useState(null);

  useEffect(() => {
    const fetchNote = async () => {
      try {
        const r = await fetch(`${API}/coach/athletes/${athlete.user_id}/note`, {
          credentials: "include",
        });
        if (r.ok) {
          const d = await r.json();
          if (d.note) {
            setMessage(d.note.message || "");
            setHasExisting(true);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    const fetchGoals = async () => {
      try {
        const r = await fetch(`${API}/coach/athletes/${athlete.user_id}/goals`, {
          credentials: "include",
        });
        if (r.ok) {
          const d = await r.json();
          setGoals((d.goals || []).slice(0, 3));
        }
      } catch {}
    };
    const fetchFood = async () => {
      try {
        const r = await fetch(`${API}/coach/athletes/${athlete.user_id}/food-log`, {
          credentials: "include",
        });
        if (r.ok) setFoodToday(await r.json());
      } catch {}
    };
    fetchNote();
    fetchGoals();
    fetchFood();
  }, [athlete.user_id]);

  const handleSave = async () => {
    if (!message.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/coach/athletes/${athlete.user_id}/note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: message.trim() }),
      });
      if (r.ok) {
        toast.success(`Note saved for ${athlete.name.split(" ")[0]}`);
        onClose();
      } else {
        toast.error("Couldn't save");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/coach/athletes/${athlete.user_id}/note`, {
        method: "DELETE",
        credentials: "include",
      });
      if (r.ok) {
        toast.success("Note removed");
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="coach-note-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-blue-400 font-bold">
              Weekly Note
            </p>
            <h3 className="text-xl font-bold text-white mt-1">
              for {athlete.name || athlete.email}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              They'll see this at the top of their dashboard all week.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
          </div>
        ) : (
          <>
            {goals.length > 0 && (
              <div className="mb-4 bg-[#0B0F1A] border border-[#1E293B] rounded-xl p-3" data-testid="coach-athlete-goals">
                <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">
                  Their Goals
                </p>
                <div className="space-y-1.5">
                  {goals.map((g) => (
                    <div key={g.goal_id} className="flex items-center justify-between text-xs gap-2">
                      <span className="text-white font-semibold truncate flex-1">{g.title}</span>
                      <span className="text-gray-400 flex-shrink-0">
                        {g.current_value} → {g.target_value} {g.unit}
                      </span>
                      <span className={`font-bold flex-shrink-0 w-10 text-right ${g.progress_pct >= 100 ? "text-emerald-400" : "text-amber-400"}`}>
                        {Math.round(g.progress_pct)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {foodToday && (foodToday.logs?.length > 0) && (
              <div className="mb-4 bg-[#0B0F1A] border border-[#1E293B] rounded-xl p-3" data-testid="coach-athlete-food">
                <p className="text-[10px] uppercase tracking-widest text-green-400 font-bold mb-2">
                  Today's Food
                </p>
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-white font-semibold">
                    {Math.round(foodToday.totals.calories)} cal
                  </span>
                  <span className="text-gray-400">
                    P {Math.round(foodToday.totals.protein_g)}g
                    · C {Math.round(foodToday.totals.carbs_g)}g
                    · F {Math.round(foodToday.totals.fat_g)}g
                  </span>
                </div>
                <p className="text-[10px] text-gray-500">
                  {foodToday.logs.length} entr{foodToday.logs.length === 1 ? "y" : "ies"} logged today
                </p>
              </div>
            )}

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hey Marcus — loved your effort on squat day. Focus this week: slow eccentrics on all main lifts."
              rows={5}
              maxLength={500}
              className="w-full px-4 py-3 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white placeholder-gray-600 focus:border-blue-500 outline-none resize-none"
              data-testid="coach-note-input"
            />
            <div className="flex items-center justify-between mt-2 mb-4">
              <span className="text-[10px] text-gray-500">{message.length}/500</span>
              {hasExisting && (
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                >
                  Remove note
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-[#1E293B] hover:bg-[#2D3B4F] text-gray-300 rounded-lg font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !message.trim()}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg font-semibold text-sm shadow-lg shadow-blue-500/25 disabled:opacity-60"
                data-testid="save-coach-note"
              >
                {saving ? "Saving..." : hasExisting ? "Update note" : "Save note"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AtRiskAthletesPanel;
